import { useState, useEffect } from "react"
import { useUpdateTicketMutation, useDeleteTicketMutation } from "./ticketsApiSlice"
import { useNavigate } from "react-router-dom"
import useAuth from "../../hooks/useAuth"

const EditTicketForm = ({ ticket, users }) => {

    const { isProductManager, isSystemAnalyst } = useAuth()

    const [updateTicket, {
        isLoading,
        isSuccess,
        isError,
        error
    }] = useUpdateTicketMutation()

    const [deleteTicket, {
        isSuccess: isDelSuccess,
        isError: isDelError,
        error: delerror
    }] = useDeleteTicketMutation()

    const navigate = useNavigate()

    const [title, setTitle] = useState(ticket.title)
    const [text, setText] = useState(ticket.text)
    const [completed, setCompleted] = useState(ticket.completed)
    const [userId, setUserId] = useState(ticket.user)

    useEffect(() => {

        if (isSuccess || isDelSuccess) {
            setTitle('')
            setText('')
            setUserId('')
            navigate('/dash/tickets')
        }

    }, [isSuccess, isDelSuccess, navigate])

    const onTitleChanged = e => setTitle(e.target.value)
    const onTextChanged = e => setText(e.target.value)
    const onCompletedChanged = e => setCompleted(prev => !prev)
    const onUserIdChanged = e => setUserId(e.target.value)

    const canSave = [title, text, userId].every(Boolean) && !isLoading

    const onSaveTicketClicked = async (e) => {
        if (canSave) {
            await updateTicket({ id: ticket.id, user: userId, title, text, completed })
        }
    }

    const onDeleteTicketClicked = async () => {
        await deleteTicket({ id: ticket.id })
    }

    const created = new Date(ticket.createdAt).toLocaleString('en-US', { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' })
    const updated = new Date(ticket.updatedAt).toLocaleString('en-US', { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' })

    const options = users.map(user => {
        return (
            <option
                key={user.id}
                value={user.id}

            > {user.username}</option >
        )
    })

    const errClass = (isError || isDelError) ? "errmsg" : "offscreen"
    const validTitleClass = !title ? "form__input--incomplete" : ''
    const validTextClass = !text ? "form__input--incomplete" : ''

    const errContent = (error?.data?.message || delerror?.data?.message) ?? ''

    // delete button can only be seen by managers and admins
    let deleteButton = null
    if (isProductManager || isSystemAnalyst) {
        deleteButton = (
            <button
                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                title="Delete"
                onClick={onDeleteTicketClicked}
            >
                Delete
            </button>
        )
    }

    const content = (
        <>
            <div className="text-center">
                <p className={errClass}>{errContent}</p>

                <form className="form" onSubmit={e => e.preventDefault()}>

                    {/* EditTicket Title */}

                    <div>
                        <h2 className="text-2xl font-bold">Edit Ticket #{ticket.ticket}</h2>
                        <div>
                            <label className="float-left ml-[1rem] md:mr-[38.4rem] md:float-none" htmlFor="ticket-title">
                                Title:
                            </label>
                        </div>
                        <div>
                            <input
                                className={`text-black w-[23rem] md:w-[40rem] py-1 pl-2 rounded-md ${validTitleClass}`}
                                id="ticket-title"
                                name="title"
                                type="text"
                                autoComplete="off"
                                value={title}
                                onChange={onTitleChanged}
                            />
                        </div>
                    </div>

                    {/* EditTicket TextArea */}
                    <div className="">
                        <div>
                            <label className="float-left ml-[1rem] md:mr-[38.4rem] md:float-none" htmlFor="ticket-text">
                                Text:
                            </label>
                        </div>
                        <div>
                            <textarea
                                className={`text-black pl-2 w-[23rem] md:w-[40rem] rounded-md ${validTextClass}`}
                                id="ticket-text"
                                name="text"
                                value={text}
                                onChange={onTextChanged}
                                style={{ maxHeight: "20rem", overflowY: "auto", resize: "none" }}
                            />

                        </div>
                    </div>


                    <div className="">

                        <div className="">
                            {/* EditTicket Completed */}
                            <label className="ml-[1rem] md:ml-[20rem] pt-[1rem]" style={{ display: 'flex', alignItems: 'center' }} htmlFor="ticket-completed">
                                Work Complete: &nbsp;
                                <input
                                    className="w-5 h-5"
                                    id="ticket-completed"
                                    name="completed"
                                    type="checkbox"
                                    checked={completed}
                                    onChange={onCompletedChanged}
                                />
                            </label>

                            {/* EditTicket Assigned */}
                            <div className="flex ml-[1rem] md:ml-[20rem] pt-[1rem]">
                                <div>
                                    <label htmlFor="ticket-username">
                                        Assigned:
                                    </label>
                                </div>

                                <div className="ml-2">
                                    <select
                                        id="ticket-username"
                                        name="username"
                                        className="text-black w-[10rem] py-1 pl-2 rounded-md text-center"
                                        value={userId}
                                        onChange={onUserIdChanged}
                                    >
                                        {options}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* EditTicket TimeStamp */}
                        <div className="flex ml-[1rem] md:ml-[20rem] pt-[1rem]">
                            <p className="underline">Created:&nbsp;{created}</p>
                        </div>
                        <div className="flex ml-[1rem] md:ml-[20rem] pt-[1rem]">
                            <p className="underline">Updated:&nbsp;{updated}</p>
                        </div>

                        {/* EditTicket Actions */}
                        <div className="mt-[4rem] md:mt-[12rem] ml-[14rem] md:ml-[69rem] gap-2">
                            <div className="flex gap-2 w-[5rem]">
                                <button
                                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                                    title="Save"
                                    onClick={onSaveTicketClicked}
                                    disabled={!canSave}>
                                    Save
                                </button>
                                {deleteButton}
                            </div>
                        </div>

                    </div>
                </form>
            </div>
        </>
    )

    return content
}

export default EditTicketForm